import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import StarRating from './StarRating';
import VideoRecorder, { RecordedVideo } from './VideoRecorder';
import { Button } from '../UI';

interface TestimonialFormProps {
  onCancel: () => void;
  onSuccess: () => void;
}

const inputClass =
  'field';

const TestimonialForm: React.FC<TestimonialFormProps> = ({ onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ author: '', text: '', country: '', email: '', rating: 5 });
  const [video, setVideo] = useState<RecordedVideo | null>(null);
  const [consent, setConsent] = useState(false);
  const [consentMedia, setConsentMedia] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending'>('idle');
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState('');

  const sending = status === 'sending';
  const text = form.text.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.author.trim() || (!text && !video)) {
      setError(t('testimonialsUi.errRequired'));
      return;
    }
    if (!video && text.length < 10) {
      setError(t('testimonialsUi.errMin'));
      return;
    }
    if (video && !consent) {
      setError(t('testimonialsUi.errConsent'));
      return;
    }

    setStatus('sending');
    setUploadPct(0);
    try {
      if (video) {
        const data = new FormData();
        data.append('author', form.author.trim());
        if (text) data.append('text', text);
        if (form.country.trim()) data.append('country', form.country.trim());
        if (form.email.trim()) data.append('email', form.email.trim());
        data.append('rating', String(form.rating));
        data.append('consentImage', 'true');
        if (consentMedia) data.append('consentMedia', 'true');
        data.append('durationLimit', String(video.limitSec));
        data.append('video', video.blob, video.fileName);
        await axios.post('/testimonials', data, {
          timeout: 300000,
          onUploadProgress: (ev) => {
            if (ev.total) setUploadPct(Math.round((ev.loaded / ev.total) * 100));
          },
        });
      } else {
        await axios.post('/testimonials', {
          author: form.author.trim(),
          text,
          country: form.country.trim() || undefined,
          email: form.email.trim() || undefined,
          rating: form.rating,
          consentMedia: consentMedia || undefined,
        });
      }
      setForm({ author: '', text: '', country: '', email: '', rating: 5 });
      setVideo(null);
      setConsent(false);
      setConsentMedia(false);
      onSuccess();
    } catch (err: any) {
      const msg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.message || t('testimonialsUi.errGeneric');
      setError(msg);
    } finally {
      setStatus('idle');
    }
  };

  const submitLabel = !sending
    ? t('testimonialsUi.submit')
    : video
      ? uploadPct < 100
        ? t('testimonialsUi.uploading', { pct: uploadPct })
        : t('testimonialsUi.processing')
      : t('testimonialsUi.sending');

  return (
    <div className="max-w-xl mx-auto card text-left shadow-lg w-full">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('testimonialsUi.formTitle')}</h3>
      <p className="text-sm text-gray-500 mb-4">{t('testimonialsUi.formIntro')}</p>
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="tf-author" className="block text-sm font-medium text-gray-700 mb-1">{t('testimonialsUi.name')} *</label>
          <input
            id="tf-author"
            type="text"
            value={form.author}
            onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
            className={inputClass}
            placeholder={t('testimonialsUi.namePh')}
            maxLength={120}
            required
          />
        </div>
        <div>
          <label htmlFor="tf-email" className="block text-sm font-medium text-gray-700 mb-1">{t('testimonialsUi.email')}</label>
          <input
            id="tf-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className={inputClass}
            placeholder={t('testimonialsUi.emailPh')}
          />
          <p className="text-xs text-gray-500 mt-1">{t('testimonialsUi.emailHint')}</p>
        </div>
        <div>
          <label htmlFor="tf-country" className="block text-sm font-medium text-gray-700 mb-1">{t('testimonialsUi.country')}</label>
          <input
            id="tf-country"
            type="text"
            value={form.country}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
            className={inputClass}
            placeholder={t('testimonialsUi.countryPh')}
            maxLength={120}
          />
        </div>
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">{t('testimonialsUi.rating')}</span>
          <StarRating value={form.rating} onChange={(rating) => setForm((f) => ({ ...f, rating }))} size="lg" label={t('testimonialsUi.rating')} />
        </div>
        <div>
          <label htmlFor="tf-text" className="block text-sm font-medium text-gray-700 mb-1">
            {t('testimonialsUi.text')}{video ? '' : ' *'}
          </label>
          <textarea
            id="tf-text"
            value={form.text}
            onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
            className={`${inputClass} min-h-[100px]`}
            placeholder={t('testimonialsUi.textPh')}
            maxLength={2000}
            rows={4}
          />
          <p className="text-xs text-gray-500 mt-1">
            {video ? t('testimonialsUi.textHintWithVideo') : t('testimonialsUi.textHint')}
          </p>
        </div>

        <VideoRecorder value={video} onChange={setVideo} disabled={sending} />

        {video && (
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="check-input mt-0.5"
            />
            <span>{t('testimonialsUi.consent')}</span>
          </label>
        )}

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={consentMedia}
            onChange={(e) => setConsentMedia(e.target.checked)}
            className="check-input mt-0.5"
          />
          <span>
            {t('testimonialsUi.consentMedia')}{' '}
            <span className="text-gray-500">{t('testimonialsUi.consentMediaOptional')}</span>
          </span>
        </label>

        {sending && video && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200" aria-hidden="true">
            <div
              className={`h-full bg-primary-600 transition-[width] duration-200 ${uploadPct >= 100 ? 'animate-pulse' : ''}`}
              style={{ width: `${uploadPct}%` }}
            />
          </div>
        )}

        <div className="form-actions pt-2">
          <Button variant="outline" type="button" onClick={onCancel} disabled={sending}>
            {t('testimonialsUi.cancel')}
          </Button>
          <Button type="submit" disabled={sending}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TestimonialForm;
